import {
  RECEIVE_AND_DEFER_SEARCH_RESPONSE,
  RECEIVE_PRODUCT_RELATIONS,
  RECEIVE_SEARCH_RESPONSE,
  REQUEST_SEARCH,
  SEARCH_FAILED,
  SET_SEARCH_OOS_MESSAGING,
  TRACK_MSFT_AD_IMPRESSIONS,
  UPDATE_SEARCH_LIMIT
} from 'constants/reduxActions';
import { createViewSearchPageMicrosoftUetEvent, pushMicrosoftUetEvent } from 'actions/microsoftUetTag';
import { firePixelServer } from 'actions/pixelServer';
import { trackError } from 'helpers/ErrorUtils';
import { formatMicrosoftPixelData } from 'helpers/SearchUtils';
import timedFetch from 'middleware/timedFetch';
import { fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import { fetchProductRelations } from 'apis/calypso';
import { selectCalypsoConfig } from 'selectors/environment';

export function requestSearch({ url, isFresh }) {
  return {
    type: REQUEST_SEARCH,
    url,
    isFresh
  };
}

export function receiveSearchResponse(response) {
  return {
    type: RECEIVE_SEARCH_RESPONSE,
    response
  };
}

/**
 * The primary search request failed but is recoverable. On the server this drives the loading state
 * (a 2xx with the loading state in the initial HTML); after hydration the client re-issues the fetch,
 * which retries internally. A terminal failure (a non-retryable failure, or every retry exhausted)
 * instead dispatches `setError` to render the full-page error.
 */
export function searchFailed() {
  return {
    type: SEARCH_FAILED
  };
}

export function setOosMessaging(response) {
  return {
    type: SET_SEARCH_OOS_MESSAGING,
    response
  };
}

export function fireSearchPixels({ term, results, pixelFacetData, filters, searchResultsCount, customerId, isVip, directedId, pageNumber }) {
  results = results || [];
  const searchPath = filters?.executedSearchUrl?.match(/\/filters\/([^/]+)\//)?.[1];
  const autoCorrectTerm = filters?.autocorrect?.termBeforeAutocorrect;

  return dispatch => {
    const microsoftUetEvent = createViewSearchPageMicrosoftUetEvent(
      term,
      results.map(result => result.productId)
    );
    dispatch(pushMicrosoftUetEvent(microsoftUetEvent));

    // used for amethyst
    const eventData = {
      stringResults: formatMicrosoftPixelData({ filters, results }),
      results: results.map(result => ({
        sku: result.productId,
        styleId: result.styleId
      })),
      facets: pixelFacetData,
      term
    };

    // used for datalayer / pixel / gtm events
    const pixelEventData = {
      ...eventData,
      autocorrected_term: autoCorrectTerm ?? '',
      search_results: searchResultsCount,
      autocompleted_term: term !== autoCorrectTerm && autoCorrectTerm ? true : false,
      pageNumber: pageNumber ? pageNumber + 1 : 1, // page number is 0 indexed..
      screen: {
        type: 'search',
        name: searchPath ? `search:${searchPath}` : ''
      },
      user: {
        login_status: customerId ? 'logged-in' : 'logged-out',
        login_type: customerId ? 'amazon' : '',
        vip_status: isVip ? 'enrolled' : '',
        directedID: directedId ?? ''
      }
    };

    dispatch(firePixelServer('search', eventData, '', pixelEventData));
  };
}

export function updateProductLimit(limit) {
  return {
    type: UPDATE_SEARCH_LIMIT,
    limit
  };
}

export function receiveAndDeferSearchResponse(deferredSearchResponse) {
  return {
    type: RECEIVE_AND_DEFER_SEARCH_RESPONSE,
    deferredSearchResponse
  };
}

export function getProductRelations(styleIds = [], getCall = fetchProductRelations) {
  return (dispatch, getState) => {
    const state = getState();
    const calypsoConfig = selectCalypsoConfig(state);

    if (styleIds.length === 0) {
      return;
    }

    return getCall(calypsoConfig, styleIds)
      .then(fetchErrorMiddleware)
      .then(response => {
        dispatch(receiveProductRelations(response));
      })
      .catch(e => {
        trackError('NON-FATAL', 'Could not retrieve product relations', e);
      });
  };
}

export function receiveProductRelations(response) {
  return {
    type: RECEIVE_PRODUCT_RELATIONS,
    response
  };
}

export const trackSponsoredAdImpressions =
  ({ url }, fetcher = timedFetch) =>
  dispatch => {
    const fetch = fetcher('SponsoredAdImpressions');
    dispatch({ type: TRACK_MSFT_AD_IMPRESSIONS });
    fetch(url).catch(error => trackError('ERROR', 'Failed to send ad impression events', error));
  };
